import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { queryKeys } from '@/query/keys';

import {
  adminEmailEndpoints,
  type EmailServerDto,
  type EmailServerStatusDto,
  type EmailTestResultDto,
} from './endpoints';

/** Normalised view model — the wire DTO is all-optional; the UI wants non-null. */
export interface EmailServerViewModel {
  readonly id: string;
  readonly name: string;
  readonly region: string;
  readonly fromAddress: string;
  readonly fromName: string;
  readonly replyTo: string;
  readonly configurationSet: string;
  readonly accessKeyId: string;
  readonly hasSecret: boolean;
  readonly isDefault: boolean;
  readonly active: boolean;
  readonly snsTopicArnAllowlist: string;
  readonly feedbackQueueUrl: string;
  readonly feedbackQueueRegion: string;
}

export function mapEmailServer(dto: EmailServerDto): EmailServerViewModel {
  return {
    id: dto.id ?? '',
    name: dto.name ?? '',
    region: dto.region ?? '',
    fromAddress: dto.from_address ?? '',
    fromName: dto.from_name ?? '',
    replyTo: dto.reply_to ?? '',
    configurationSet: dto.configuration_set ?? '',
    accessKeyId: dto.access_key_id ?? '',
    hasSecret: dto.has_secret ?? false,
    isDefault: dto.is_default ?? false,
    active: dto.active ?? true,
    snsTopicArnAllowlist: dto.sns_topic_arn_allowlist ?? '',
    feedbackQueueUrl: dto.feedback_queue_url ?? '',
    feedbackQueueRegion: dto.feedback_queue_region ?? '',
  };
}

export interface EmailServerInput {
  readonly name: string;
  readonly region: string;
  readonly fromAddress: string;
  readonly fromName: string;
  readonly replyTo: string;
  readonly configurationSet: string;
  readonly accessKeyId: string;
  /** Plaintext; empty on update means "keep the stored secret". */
  readonly secretAccessKey: string;
  readonly isDefault: boolean;
  readonly active: boolean;
  readonly snsTopicArnAllowlist: string;
  readonly feedbackQueueUrl: string;
  readonly feedbackQueueRegion: string;
}

export interface UpdateEmailServerInput extends EmailServerInput {
  readonly id: string;
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function toDto(input: EmailServerInput, id?: string): EmailServerDto {
  const dto: EmailServerDto = {
    ...(id !== undefined ? { id } : {}),
    name: input.name.trim(),
    region: input.region.trim(),
    from_address: input.fromAddress.trim(),
    from_name: blankToUndefined(input.fromName) ?? null,
    reply_to: blankToUndefined(input.replyTo) ?? null,
    configuration_set: blankToUndefined(input.configurationSet) ?? null,
    access_key_id: blankToUndefined(input.accessKeyId) ?? null,
    is_default: input.isDefault,
    active: input.active,
    sns_topic_arn_allowlist: blankToUndefined(input.snsTopicArnAllowlist) ?? null,
    feedback_queue_url: blankToUndefined(input.feedbackQueueUrl) ?? null,
    feedback_queue_region: blankToUndefined(input.feedbackQueueRegion) ?? null,
  };
  const secret = blankToUndefined(input.secretAccessKey);
  if (secret !== undefined) {
    dto.secret_access_key = secret;
  }
  return dto;
}

export function useAdminEmailServers(): UseQueryResult<EmailServerViewModel[]> {
  return useQuery<EmailServerViewModel[]>({
    queryKey: queryKeys.emailServers,
    queryFn: async ({ signal }) => {
      const page = await adminEmailEndpoints.getEmailServers({ size: 200, signal });
      return page.content.map(mapEmailServer);
    },
  });
}

export function useEmailServerStatus(id: string): UseQueryResult<EmailServerStatusDto> {
  return useQuery<EmailServerStatusDto>({
    queryKey: queryKeys.emailServerStatus(id),
    queryFn: ({ signal }) => adminEmailEndpoints.getEmailServerStatus(id, { signal }),
    staleTime: 60 * 1000,
    retry: false,
  });
}

/** Test-send mutation — resolves with the result DTO (ok=false is a normal, non-throwing outcome). */
export function useEmailServerTest(): UseMutationResult<
  EmailTestResultDto,
  Error,
  { id: string; to: string }
> {
  return useMutation<EmailTestResultDto, Error, { id: string; to: string }>({
    mutationFn: ({ id, to }) => adminEmailEndpoints.testEmailServer(id, { to }),
  });
}

export function useAdminEmailServerMutations(): {
  readonly create: UseMutationResult<EmailServerViewModel, Error, EmailServerInput>;
  readonly update: UseMutationResult<EmailServerViewModel, Error, UpdateEmailServerInput>;
  readonly remove: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.emailServers }).catch(() => undefined);
  };

  const create = useMutation<EmailServerViewModel, Error, EmailServerInput>({
    mutationFn: async (input) => mapEmailServer(await adminEmailEndpoints.createEmailServer(toDto(input))),
    onSettled: invalidate,
  });

  const update = useMutation<EmailServerViewModel, Error, UpdateEmailServerInput>({
    mutationFn: async ({ id, ...rest }) =>
      mapEmailServer(await adminEmailEndpoints.updateEmailServer(id, toDto(rest, id))),
    onSettled: invalidate,
  });

  const remove = useMutation<undefined, Error, string>({
    mutationFn: (id) => adminEmailEndpoints.deleteEmailServer(id),
    onSettled: invalidate,
  });

  return { create, update, remove };
}
